import React, { useMemo } from 'react';
import { MenuItem } from '@mui/material';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';

import { ParentReviewService } from 'us-web-services';
import useUser from '../../store/useUser';
import DateUtil from '../../util/DateUtil';
import DisplayService from '../../util/DisplayService';
import { Table, TableInstance } from '../common/Table';

function ParentReviews() {
  const tableRef = React.useRef<TableInstance>();
  const userState = useUser()[0];
  const params = () => {
    const paramArray = [];

    if (userState.user.parent) {
      paramArray.push({
        key: 'parentId',
        value: userState.user.id,
      });
    } else {
      paramArray.push({
        key: 'sitterId',
        value: userState.user.id,
      });
    }

    return paramArray;
  };

  const showError = error => {
    const displayedError = DisplayService.getErrorResponse(
      error,
      'There was an error with the review.',
    );

    toast.error(displayedError.message);
  };

  const refreshReviews = () => tableRef.current?.fetchData();

  const deleteReview = async rowData => {
    ParentReviewService.delete(rowData.id)
      .then(refreshReviews)
      .catch(showError);
  };

  const updateSuccess = () => {
    toast.success('Review updated successfully');
    refreshReviews();
  };

  const updateReview = async rowData => {
    const data = {
      id: rowData.row.id,
      recommendation: {
        id: rowData.values['recommendation.id'],
      },
      visibility: {
        id: rowData.values['visibility.id'],
      },
      respectsYourTime: rowData.values.respectsYourTime,
      accurateJobDescription: rowData.values.accurateJobDescription,
      paysBasedOnBooking: rowData.values.paysBasedOnBooking,
    };

    ParentReviewService.patch(rowData.row.id, data)
      .then(updateSuccess)
      .catch(showError);
  };

  const recommendationSelect = [
    { id: 1, name: 'Yes, I would' },
    { id: 2, name: 'Not a fit for me' },
    { id: 3, name: 'No sitters should' },
  ];

  const getRecommendation = recommendationId =>
    recommendationSelect.find(item => item.id === recommendationId);

  const visibilitySelect = [
    { id: 1, name: 'Public' },
    { id: 2, name: 'Private' },
  ];
  const getVisibility = visibilityId =>
    visibilitySelect.find(item => item.id === visibilityId);

  const yesNoSelect = [
    { id: true, name: 'Yes' },
    { id: false, name: 'No' },
  ];
  const getYesNoEdit = () => ({
    select: true,
    children: yesNoSelect.map(item => (
      // @ts-expect-error added key attribute necessary here for React
      <MenuItem key={item.id} value={item.id}>
        {item.name}
      </MenuItem>
    )),
  });

  const getYesNoDisplay = ({ cell }) => (
    <span>{cell.getValue() ? 'Yes' : 'No'}</span>
  );

  const getProfileLink = ({ cell }) => (
    <Link to={`/users/lookup?id=${cell.getValue().id}`}>
      {cell.getValue().firstName} {cell.getValue().lastName}
    </Link>
  );

  const getReadableDateTime = timestamp =>
    DateUtil.getReadableDateTime(timestamp);

  const getDateTimeDisplay = ({ cell }) => (
    <>{getReadableDateTime(cell.getValue())}</>
  );

  const COLUMNS = useMemo(
    () => [
      {
        id: 'parent-reviews',
        header: 'Parent Reviews',
        columns: [
          {
            header: 'Sitter Name',
            enableEditing: false,
            accessorFn: row => row.sitter,
            Cell: getProfileLink,
            Edit: ({ cell }) => (
              <>
                <b>Sitter Name</b>
                <br />
                {cell.getValue().firstName} {cell.getValue().lastName}
                <br />
              </>
            ),
          },
          {
            header: 'Parent Name',
            enableEditing: false,
            accessorFn: row => row.parent,
            Cell: getProfileLink,
            Edit: ({ cell }) => (
              <>
                <b>Parent Name</b>
                <br />
                {cell.getValue().firstName} {cell.getValue().lastName}
                <br />
              </>
            ),
          },
          {
            header: 'Response Category',
            accessorKey: 'recommendation.id',
            Cell: ({ cell }) => <>{getRecommendation(cell.getValue()).name}</>,
            muiTableBodyCellEditTextFieldProps: {
              select: true,
              children: recommendationSelect.map(item => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              )),
            },
          },
          {
            header: 'Respects Your time',
            accessorKey: 'respectsYourTime',
            Cell: getYesNoDisplay,
            muiTableBodyCellEditTextFieldProps: getYesNoEdit(),
          },
          {
            header: 'Accurate Job Desc.',
            accessorKey: 'accurateJobDescription',
            Cell: getYesNoDisplay,
            muiTableBodyCellEditTextFieldProps: getYesNoEdit(),
          },
          {
            header: 'Pays based on booking',
            accessorKey: 'paysBasedOnBooking',
            Cell: getYesNoDisplay,
            muiTableBodyCellEditTextFieldProps: getYesNoEdit(),
          },
          {
            header: 'Safety Concern',
            accessorKey: 'safetyConcern',
            Cell: getYesNoDisplay,
            muiTableBodyCellEditTextFieldProps: getYesNoEdit(),
          },
          {
            header: 'Visibility',
            accessorKey: 'visibility.id',
            Cell: ({ cell }) => <>{getVisibility(cell.getValue()).name}</>,
            muiTableBodyCellEditTextFieldProps: {
              select: true,
              children: visibilitySelect.map(item => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              )),
            },
          },
          {
            header: 'Comment',
            accessorKey: 'comment',
          },
          {
            header: 'Created',
            accessorKey: 'created',
            enableEditing: false,
            Cell: getDateTimeDisplay,
            Edit: ({ cell }) => (
              <>
                <b>Created</b>
                <br />
                {getReadableDateTime(cell.getValue())}
                <br />
              </>
            ),
          },
          {
            header: 'Updated',
            accessorKey: 'updated',
            enableEditing: false,
            Cell: getDateTimeDisplay,
            Edit: ({ cell }) => (
              <>
                <b>Updated</b>
                <br />
                {getReadableDateTime(cell.getValue())}
                <br />
              </>
            ),
          },
        ],
      },
    ],
    [],
  );

  return (
    <Table
      ref={tableRef}
      columns={COLUMNS}
      params={params}
      retrieveData={ParentReviewService.getByFilter}
      editAction={updateReview}
      deleteAction={deleteReview}
      alwaysApplyParams
      enableInlineEditing
    />
  );
}

export default ParentReviews;
